import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { EyeOff, Eye, Lock, ShieldCheck } from "lucide-react";
import { useAuth, authAxios } from "@/context/AuthContext";

/**
 * Discreet 18+ opt-in for paid members.
 * Free members see a locked "Upgrade" nudge. Non-opted-in paid members see the confirmation form.
 * Opted-in members see a subtle status pill + Manage link.
 */
export const AdultOptInCard = () => {
  const { user, refreshUser } = useAuth();
  const [expand, setExpand] = useState(false);
  const [confirm, setConfirm] = useState({ age: false, terms: false });
  const [busy, setBusy] = useState(false);

  const isPaid = !!user?.is_paid;
  const isOpted = !!user?.adult_opt_in;

  const enable = async () => {
    if (!confirm.age || !confirm.terms) { toast.error("Please confirm both statements"); return; }
    setBusy(true);
    try {
      await authAxios.post("/user/adult-opt-in", { over_18_confirmed: true, accept_terms: true });
      toast.success("Adult section unlocked");
      refreshUser?.();
      setExpand(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enable");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Hide the Adult section again?")) return;
    setBusy(true);
    try {
      await authAxios.post("/user/adult-opt-out");
      toast.success("Adult section hidden");
      refreshUser?.();
    } catch (err) {
      console.error("opt-out failed:", err);
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="adult-optin-card"
      className="bg-slate-900 text-slate-200 border border-slate-800 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
          {isOpted ? <Eye className="w-5 h-5 text-rose-400" /> : <EyeOff className="w-5 h-5 text-slate-400" />}
        </div>
        <div>
          <p className="font-semibold text-sm text-white">
            {isOpted ? "Adult (18+) affiliates enabled" : "Enable Adult (18+) affiliates"}
          </p>
          <p className="text-xs text-slate-400 leading-relaxed mt-0.5 max-w-lg">
            {isPaid
              ? isOpted
                ? "You have access to the Adult section. Contact admin for the current list of high-payout private offers."
                : "Members-only high-payout private offers. Discreet · 18+ · Opt-in only."
              : "Available only to Members Club subscribers. Upgrade to unlock the highest-payout private offers."}
          </p>
        </div>
      </div>

      {!isPaid ? (
        <Link
          to="/membership"
          data-testid="adult-upgrade-cta"
          className="text-xs font-semibold px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 inline-flex items-center gap-1.5 flex-shrink-0"
        >
          <Lock className="w-3.5 h-3.5" /> Unlock $1.99/mo
        </Link>
      ) : isOpted ? (
        <div className="flex gap-2 flex-shrink-0">
          <Link
            to="/adult"
            data-testid="adult-open-link"
            className="text-xs font-semibold px-4 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-500"
          >
            Open section
          </Link>
          <button
            onClick={disable}
            disabled={busy}
            data-testid="adult-disable-button"
            className="text-xs px-3 py-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
          >
            Hide
          </button>
        </div>
      ) : (
        <button
          onClick={() => setExpand(!expand)}
          data-testid="adult-enable-toggle"
          className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-700 text-slate-200 hover:border-slate-500 flex-shrink-0"
        >
          {expand ? "Cancel" : "Turn on"}
        </button>
      )}

      {expand && !isOpted && isPaid && (
        <div className="w-full mt-4 p-4 bg-slate-950 border border-slate-800 rounded-lg" data-testid="adult-confirm-form">
          <label className="flex gap-2 text-xs text-slate-300 mb-2 cursor-pointer">
            <input
              data-testid="adult-age-check"
              type="checkbox"
              checked={confirm.age}
              onChange={(e) => setConfirm({ ...confirm, age: e.target.checked })}
              className="mt-0.5"
            />
            <span>I confirm I am 18 years of age or older.</span>
          </label>
          <label className="flex gap-2 text-xs text-slate-300 mb-3 cursor-pointer">
            <input
              data-testid="adult-terms-check"
              type="checkbox"
              checked={confirm.terms}
              onChange={(e) => setConfirm({ ...confirm, terms: e.target.checked })}
              className="mt-0.5"
            />
            <span>I understand this section contains adult (18+) affiliate offers and I want to see them.</span>
          </label>
          <button
            data-testid="adult-confirm-enable-button"
            onClick={enable}
            disabled={busy || !confirm.age || !confirm.terms}
            className="w-full py-2 rounded-full bg-rose-600 text-white text-sm font-bold hover:bg-rose-500 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" /> Enable Adult section
          </button>
        </div>
      )}
    </section>
  );
};
